import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRecordsApi } from "../../lib/api";
import { RecordsTable } from "../../components/admin/records/RecordsTable";
import { Loader2, Plus, Search, ArrowRight, Archive } from "lucide-react";
import { Link } from "react-router-dom";
import type { DocumentRecord, RecordResponse } from "../../types/record";
import type { SearchResponse } from "../../types/search";
import { cn } from "../../lib/utils";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const SEARCH_MODES = [
  { value: "semantic", label: "Semantic" },
  { value: "keywords", label: "Keywords" },
  { value: "id", label: "#" },
] as const;

type SearchMode = (typeof SEARCH_MODES)[number]["value"];

interface PaginationProps {
  currentPage: number;
  pageSize: number;
  total: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

function Pagination({
  currentPage,
  pageSize,
  total,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const from = (currentPage - 1) * pageSize;
  const to = Math.min(from + pageSize, total);
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 2)}
          disabled={!hasPreviousPage}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage)}
          disabled={!hasNextPage}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-700">
            {total > 0 ? (
              <>
                Showing <span className="font-medium">{from + 1}</span> to{" "}
                <span className="font-medium">{to}</span> of{" "}
                <span className="font-medium">{total}</span> results
              </>
            ) : (
              "No results found"
            )}
          </p>
          <div className="flex items-center gap-2">
            <label htmlFor="pageSize" className="text-sm text-gray-600">
              Show
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={onPageSizeChange}
              className="rounded-md border-gray-300 py-1 pl-2 pr-8 text-sm focus:border-purple-500 focus:ring-purple-500"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600">per page</span>
          </div>
        </div>
        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(currentPage - 2)}
              disabled={!hasPreviousPage}
              className="relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:hover:bg-white"
            >
              Previous
            </button>
            <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage)}
              disabled={!hasNextPage}
              className="relative inline-flex items-center rounded-r-md px-4 py-2 text-sm font-medium text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:hover:bg-white"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

export function RecordsPage() {
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [searchMode, setSearchMode] = useState<SearchMode>("semantic");
  const [showArchived, setShowArchived] = useState(false);
  const [pageSize, setPageSize] =
    useState<(typeof PAGE_SIZE_OPTIONS)[number]>(20);
  const [sortField, setSortField] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [showArrow, setShowArrow] = useState(Math.random() < 0.5);
  const [searchById, setSearchById] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const recordsApi = useRecordsApi();

  const { data, isLoading, isError, refetch } = useQuery<
    RecordResponse | SearchResponse
  >({
    queryKey: [
      "admin-records",
      page,
      pageSize,
      activeSearch,
      searchMode,
      sortField,
      sortDirection,
      showArchived,
      searchById,
    ],
    queryFn: async () => {
      try {
        if (searchById) {
          try {
            const record = await recordsApi.getRecord(searchById);
            setSearchError(null);
            return {
              results: [record],
              total: 1,
              page: 0,
              size: 1,
              pages: 1,
              has_next: false,
              has_prev: false,
            };
          } catch (error: any) {
            if (error?.response?.status === 404) {
              setSearchError(`Document ${searchById} not found`);
              // Reset search and return all records
              setSearchById("");
              return recordsApi.getAllRecords(
                page,
                pageSize,
                sortField,
                sortDirection,
                showArchived
              );
            }
            throw error;
          }
        }
        if (activeSearch) {
          return recordsApi.searchRecords(
            activeSearch,
            page,
            pageSize,
            searchMode === "semantic"
          );
        }
        return recordsApi.getAllRecords(
          page,
          pageSize,
          sortField,
          sortDirection,
          showArchived
        );
      } catch (error) {
        console.error("Error fetching records:", error);
        throw error;
      }
    },
  });

  // Handle unarchive
  const handleUnarchiveRecord = async (id: number): Promise<void> => {
    try {
      await recordsApi.unarchiveRecord(id);
      refetch(); // Refresh the list after unarchiving
    } catch (error) {
      console.error("Failed to unarchive record:", error);
      // You might want to show an error toast here
    }
  };

  // Reset to first page when search query changes
  useEffect(() => {
    setPage(0);
  }, [activeSearch]);

  // Reset error when changing search
  useEffect(() => {
    setSearchError(null);
  }, [searchQuery]);

  // Validation function for tsquery syntax
  const isValidTsQuery = (query: string): boolean => {
    if (!query.trim()) return false;

    // Check for balanced parentheses
    let parenthesesCount = 0;
    for (const char of query) {
      if (char === "(") parenthesesCount++;
      if (char === ")") parenthesesCount--;
      if (parenthesesCount < 0) return false;
    }
    if (parenthesesCount !== 0) return false;

    // Check for valid operator usage
    const operators = ["&", "|"];

    // Split by operators but keep them in the array
    const parts = query
      .split(/(\s*[&|]\s*)/)
      .map((p) => p.trim())
      .filter(Boolean);

    // Can't start or end with an operator
    if (
      operators.includes(parts[0]) ||
      operators.includes(parts[parts.length - 1])
    ) {
      return false;
    }

    // Can't have two operators in a row
    for (let i = 0; i < parts.length - 1; i++) {
      if (operators.includes(parts[i]) && operators.includes(parts[i + 1])) {
        return false;
      }
    }

    // Check that each term is a single word (no spaces)
    for (const part of parts) {
      if (!operators.includes(part) && part.includes(" ")) {
        return false;
      }
    }

    return true;
  };

  const handleSearchSubmit = () => {
    if (searchMode === "id") {
      setSearchById(searchQuery.trim());
      setActiveSearch("");
    } else if (searchMode === "semantic" || isValidTsQuery(searchQuery)) {
      setSearchById("");
      setActiveSearch(searchQuery);
    }
    // Toggle the icon for next search
    setShowArrow(Math.random() < 0.5);
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleUpdateRecord = async (id: string, updates: any) => {
    try {
      await recordsApi.updateRecord(id, updates);
      refetch();
    } catch (error) {
      console.error("Failed to update record:", error);
    }
  };

  const handleDeleteRecord = async (id: string) => {
    try {
      await recordsApi.deleteRecord(id);
      refetch();
    } catch (error) {
      console.error("Failed to delete record:", error);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newSize = parseInt(
      event.target.value
    ) as (typeof PAGE_SIZE_OPTIONS)[number];
    setPageSize(newSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
  };

  const searchPreview = useMemo(() => {
    if (!searchQuery.trim() || searchMode === "semantic") return null;

    try {
      if (!isValidTsQuery(searchQuery)) {
        return (
          <div className="mt-1 text-xs">
            <span className="text-red-500">
              Syntaxe invalide. Les termes doivent être des mots simples reliés
              par & (et) ou | (ou). Par exemple: (force & dominante) |
              (leadership & inclusif)
            </span>
          </div>
        );
      }

      // Parse the query to show preview
      const parseQuery = (query: string) => {
        return query.split(/\s*\|\s*/).map((orPart) => {
          return orPart
            .replace(/[()]/g, "")
            .split(/\s*&\s*/)
            .map((term) => term.trim())
            .filter(Boolean);
        });
      };

      const conditions = parseQuery(searchQuery);

      return (
        <div className="mt-1 text-xs flex items-center gap-1">
          <span className="text-gray-500">
            Recherche de documents contenant:
          </span>
          <div className="flex items-center flex-wrap gap-1">
            {conditions.map((andGroup, i) => (
              <span
                key={i}
                className="text-gray-700 inline-flex items-center gap-1"
              >
                {i > 0 && (
                  <span className="text-purple-500 font-medium">OU</span>
                )}
                {andGroup.map((term, j) => (
                  <span key={j} className="inline-flex items-center gap-1">
                    {j > 0 && (
                      <span className="text-purple-500 font-medium">ET</span>
                    )}
                    <code className="bg-gray-100 px-1 py-0.5 rounded">
                      {term}
                    </code>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      );
    } catch (e) {
      return (
        <div className="mt-1 text-xs">
          <span className="text-red-500">
            Erreur de syntaxe dans la requête
          </span>
        </div>
      );
    }
  }, [searchQuery, searchMode]);

  const records = useMemo(() => {
    if (!data) return [];
    return data.results.map((result) => ({
      ...result,
      meta:
        typeof result.meta === "string" ? JSON.parse(result.meta) : result.meta,
      created_at: "created_at" in result ? result.created_at : undefined,
      updated_at: "updated_at" in result ? result.updated_at : undefined,
      deleted_at: "deleted_at" in result ? result.deleted_at : undefined,
    })) as DocumentRecord[];
  }, [data]);

  const hasNextPage = data?.has_next ?? false;

  const handleSearchById = (id: string) => {
    setSearchById(id);
    setActiveSearch("");
    setPage(0);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-800">
        Failed to load records. Please try again later.
      </div>
    );
  }

  const currentPage = page + 1; // Convert 0-based to 1-based for display
  const hasPreviousPage = page > 0;
  const total = data?.total ?? 0;

  const paginationProps = {
    currentPage,
    pageSize,
    total,
    hasNextPage,
    hasPreviousPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Records Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage and organize your knowledge base records
          </p>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm",
              showArchived
                ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            )}
          >
            <Archive className="h-4 w-4" />
            {showArchived ? "Show Active" : "Show Archived"}
          </button>
          <Link
            to="/admin/records/create"
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
          >
            <Plus className="h-4 w-4" />
            Add Record
          </Link>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <div className="group flex focus-within:ring-1 focus-within:ring-purple-500 rounded-lg">
            <input
              type="text"
              placeholder={
                searchMode === "id"
                  ? "Enter record ID..."
                  : "Search records... (Press Enter to search)"
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              className="block w-full rounded-l-lg border border-r-0 border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-purple-500 focus:outline-none group-focus-within:border-purple-500"
            />
            <button
              onClick={handleSearchSubmit}
              className="inline-flex items-center justify-center rounded-r-lg border border-l-0 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none group-focus-within:border-purple-500"
            >
              {showArrow ? (
                <ArrowRight className="h-5 w-5 text-gray-400" />
              ) : (
                <Search className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>
        <select
          value={searchMode}
          onChange={(e) => setSearchMode(e.target.value as SearchMode)}
          className="h-[38px] rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        >
          {SEARCH_MODES.map((mode) => (
            <option key={mode.label} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </select>
      </div>

      {searchError && (
        <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">
          {searchError}
        </div>
      )}

      {searchMode === "keywords" && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500">
            Pour la recherche par mots-clés, utilisez & (et) et | (ou). Exemple:{" "}
            <code className="text-gray-700">
              (force & dominante) | (leadership & inclusif)
            </code>
          </p>
          {searchPreview}
        </div>
      )}

      <Pagination {...paginationProps} />

      <div className="mt-8">
        {isLoading ? (
          <div className="flex h-96 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : isError ? (
          <div className="flex h-96 items-center justify-center">
            <p className="text-sm text-gray-500">Failed to load records</p>
          </div>
        ) : (
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <RecordsTable
                    data={records}
                    onDelete={handleDeleteRecord}
                    onUpdate={handleUpdateRecord}
                    onUnarchive={handleUnarchiveRecord}
                    onSort={(field, direction) => {
                      setSortField(field);
                      setSortDirection(direction);
                    }}
                    onSearchById={handleSearchById}
                    currentSortField={sortField}
                    currentSortDirection={sortDirection}
                    showArchived={showArchived}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {data && (
          <Pagination
            currentPage={page + 1}
            pageSize={pageSize}
            total={data.total}
            hasNextPage={hasNextPage}
            hasPreviousPage={page > 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
}
